// Single source of truth for WHAT TPA stores in the browser and under which
// cookie category. Three things read it, so they cannot drift apart:
//   - the preferences modal (components/consent/CookiePreferencesModal.vue)
//   - the public Cookie Policy page (content/legal/cookiePolicy.ts)
//   - the policy version / renewal rules used by the consent store
//
// WHEN YOU ADD A COOKIE, A localStorage KEY OR A THIRD-PARTY SCRIPT:
//   1. add it to COOKIE_ITEMS below, in the right category;
//   2. bump COOKIE_POLICY_VERSION if it is not "necessary" (a new purpose the
//      visitor has not agreed to) - every visitor is then asked again;
//   3. gate the script/storage behind useConsentStore().hasConsent(category).
// Anything not "necessary" that is written WITHOUT consent breaches ePrivacy
// art. 5(3) / LSSI art. 22.2, whatever this file says.
//
// ONLY "necessary" AND "preferences" EXIST TODAY, and only "necessary" has items.
// Analytics (Google Analytics / Tag Manager) and advertising were removed because
// TPA uses neither. To bring a category back you need ALL of: the id in
// types/consent.ts (OPTIONAL_CONSENT_CATEGORIES), its copy and order here, a column
// in the API (models.ConsentRecord + a migration), its field in the API handler
// and controllers.ConsentInput / ValidateConsentInput, and the loader for whatever
// script it gates. docs/cookie-consent.md has the checklist.
//
// Text is typed Record<CookieLocale, string>, so forgetting a language is a
// compile error rather than a silent Spanish fallback.

import type { ConsentCategory } from '@/types/consent'

/**
 * Version of the cookie policy shown to visitors. Bump it whenever the set of
 * non-essential purposes changes in a way a past "yes" does not cover: stored
 * choices that name an older version are discarded and the banner returns.
 * Wording-only edits and additions of "necessary" items do NOT need a bump.
 */
export const COOKIE_POLICY_VERSION = '1.0'

/** Human-readable date the current policy text was last edited. */
export const COOKIE_POLICY_UPDATED: Record<CookieLocale, string> = {
  es: 'octubre de 2026',
  en: 'October 2026',
  fr: 'octobre 2026',
}

/**
 * How long a choice is honoured before the banner asks again. The AEPD
 * recommends not exceeding 24 months and the CNIL suggests 6; 12 sits between
 * them and matches common practice.
 */
export const CONSENT_TTL_MONTHS = 12

export type CookieLocale = 'es' | 'en' | 'fr'
export type Localized = Record<CookieLocale, string>

export type CookieStorageKind = 'cookie' | 'localStorage' | 'sessionStorage'

export interface CookieItem {
  id: string
  category: ConsentCategory
  /** Exact key(s) as stored. A trailing `*` means "plus a suffix". */
  names: string[]
  kind: CookieStorageKind
  /** "TPA" for our own storage, otherwise the third party's name. */
  provider: string
  duration: Localized
  purpose: Localized
}

export interface CookieCategoryCopy {
  title: Localized
  description: Localized
  /** Shown when the category has no items in use. */
  empty: Localized
}

export const COOKIE_CATEGORY_COPY: Record<ConsentCategory, CookieCategoryCopy> = {
  necessary: {
    title: { es: 'Necesarias', en: 'Necessary', fr: 'Nécessaires' },
    description: {
      es: 'Hacen que TPA funcione: mantienen tu sesión iniciada, recuerdan el idioma y los ajustes que eliges y guardan tu decisión sobre cookies. Sin ellas el servicio no puede prestarse, por eso no se pueden desactivar.',
      en: 'They make TPA work: they keep you signed in, remember the language and settings you choose, and store your cookie decision. Without them the service cannot be provided, so they cannot be switched off.',
      fr: "Elles font fonctionner TPA : elles maintiennent votre session ouverte, mémorisent la langue et les réglages que vous choisissez et conservent votre choix sur les cookies. Sans elles, le service ne peut pas être fourni, c'est pourquoi elles ne peuvent pas être désactivées.",
    },
    empty: { es: '', en: '', fr: '' },
  },
  preferences: {
    title: { es: 'Preferencias', en: 'Preferences', fr: 'Préférences' },
    description: {
      es: 'Recuerdan ajustes opcionales para personalizar tu experiencia, más allá de lo imprescindible para usar TPA.',
      en: 'They remember optional settings to personalise your experience, beyond what is essential to use TPA.',
      fr: 'Elles mémorisent des réglages facultatifs pour personnaliser votre expérience, au-delà de ce qui est indispensable pour utiliser TPA.',
    },
    empty: {
      es: 'Actualmente TPA no utiliza cookies de esta categoría.',
      en: 'TPA does not currently use cookies in this category.',
      fr: "TPA n'utilise actuellement aucun cookie de cette catégorie.",
    },
  },
}

/** Display order of the categories everywhere they are listed. */
export const COOKIE_CATEGORY_ORDER: ConsentCategory[] = ['necessary', 'preferences']

// Classification note for legal review: language, selected team and sidebar
// state are user-chosen interface settings. They are listed as "necessary"
// because the AEPD guide treats interface-personalisation storage that the
// user explicitly selects as exempt from consent. If counsel disagrees, move
// them to "preferences" AND gate their writes behind hasConsent('preferences').
export const COOKIE_ITEMS: CookieItem[] = [
  {
    id: 'session',
    category: 'necessary',
    names: ['authToken', 'authTokenExpiresAt', 'currentUser', 'authStore', 'userStore'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Hasta cerrar sesión', en: 'Until you sign out', fr: "Jusqu'à la déconnexion" },
    purpose: {
      es: 'Mantienen tu sesión iniciada y tu perfil mientras usas TPA.',
      en: 'Keep you signed in and hold your profile while you use TPA.',
      fr: 'Maintiennent votre session ouverte et conservent votre profil pendant que vous utilisez TPA.',
    },
  },
  {
    id: 'workspace',
    category: 'necessary',
    names: ['teamStore', 'sessionStore', 'selectedTeamId'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Hasta cerrar sesión', en: 'Until you sign out', fr: "Jusqu'à la déconnexion" },
    purpose: {
      es: 'Recuerdan qué equipo y qué sesión de diagnóstico tienes seleccionados.',
      en: 'Remember which team and diagnostic session you have selected.',
      fr: "Mémorisent l'équipe et la session de diagnostic que vous avez sélectionnées.",
    },
  },
  {
    id: 'drafts',
    category: 'necessary',
    names: ['tpa:questionnaire-draft:*', 'tpa:reminder-counts:*', 'session-ended-shown-*'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Persistente', en: 'Persistent', fr: 'Persistant' },
    purpose: {
      es: 'Guardan el borrador del cuestionario, los recordatorios enviados y los avisos ya mostrados, para que retomes donde lo dejaste.',
      en: 'Store your questionnaire draft, the reminders already sent, and the notices already shown, so you can pick up where you left off.',
      fr: 'Conservent votre brouillon de questionnaire, les rappels déjà envoyés et les avis déjà affichés, pour reprendre là où vous vous êtes arrêté.',
    },
  },
  {
    id: 'progress-flags',
    category: 'necessary',
    names: ['questionnaire-sent-*', 'feedback-submitted-*'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Hasta cerrar sesión', en: 'Until you sign out', fr: "Jusqu'à la déconnexion" },
    purpose: {
      es: 'Recuerdan si ya has enviado los recordatorios del cuestionario o tu valoración, para no repetir esos pasos.',
      en: 'Remember whether you have already sent the questionnaire reminders or your feedback, so those steps are not repeated.',
      fr: 'Mémorisent si vous avez déjà envoyé les rappels du questionnaire ou votre évaluation, pour ne pas répéter ces étapes.',
    },
  },
  {
    id: 'flow-flags',
    category: 'necessary',
    names: ['tpa.terms.downloaded', 'tpa.creditsPrompt.shown', 'preselectedTeamId'],
    kind: 'sessionStorage',
    provider: 'TPA',
    duration: { es: 'Sesión del navegador', en: 'Browser session', fr: 'Session du navigateur' },
    purpose: {
      es: 'Controlan pasos internos (aviso de créditos, lectura de las condiciones) para no repetirlos en la misma visita.',
      en: 'Track internal steps (credits notice, reading the terms) so they are not repeated during the same visit.',
      fr: 'Suivent des étapes internes (avis de crédits, lecture des conditions) pour ne pas les répéter pendant la même visite.',
    },
  },
  {
    id: 'language',
    category: 'necessary',
    names: ['tpa_locale'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Persistente', en: 'Persistent', fr: 'Persistant' },
    purpose: {
      es: 'Recuerda el idioma que has elegido.',
      en: 'Remembers the language you chose.',
      fr: 'Mémorise la langue que vous avez choisie.',
    },
  },
  {
    id: 'layout',
    category: 'necessary',
    names: ['sidebarCollapsed', 'tpa.sidebar.collapsedTeams', 'tpa.sidebar.collapsedOrgs'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Persistente', en: 'Persistent', fr: 'Persistant' },
    purpose: {
      es: 'Recuerdan si has plegado el menú lateral y sus secciones.',
      en: 'Remember whether you collapsed the side menu and its sections.',
      fr: 'Mémorisent si vous avez replié le menu latéral et ses sections.',
    },
  },
  {
    id: 'consent',
    category: 'necessary',
    names: ['tpa.cookieConsent'],
    kind: 'localStorage',
    provider: 'TPA',
    duration: { es: 'Hasta 12 meses', en: 'Up to 12 months', fr: "Jusqu'à 12 mois" },
    purpose: {
      es: 'Guarda tu decisión sobre las cookies para no volver a preguntártelo en cada visita.',
      en: 'Stores your cookie decision so we do not ask you again on every visit.',
      fr: 'Conserve votre choix sur les cookies pour ne pas vous le redemander à chaque visite.',
    },
  },
]

/**
 * Items currently in use for a category. Only list what is really set: naming a
 * cookie that is never stored would misinform the visitor as much as hiding one
 * that is.
 */
export function listCookieItems(category: ConsentCategory): CookieItem[] {
  return COOKIE_ITEMS.filter((item) => item.category === category)
}

/** Maps any app locale (ca/eu/pt included) onto a language the content exists in. */
export function toCookieLocale(locale: string): CookieLocale {
  const base = locale.toLowerCase().slice(0, 2)
  return base === 'en' || base === 'fr' ? base : 'es'
}
